import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'
import dayjs from 'dayjs'
import axios from 'axios'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
const http = new HttpService()

export interface Country {
  id: string
  code: string
  name: string
  iso_code: string
}

export interface Company {
  id: string
  code: string
  name: string
}

export interface Payroll {
  id: string
  name: string
  code: string
  companyId?: string
  frequencyId?: string
  currencyId?: string
  workingDays?: any[]
  holidayGroupId?: string
  employeeCodeType?: string
  employeeCodeConsecutive?: string
  sendReceiptEmail?: boolean
  alterNamesConcepts?: boolean
  alterCodesConcepts?: boolean
  lastPayment?: string
  lastBonus?: string
  calendarDays?: boolean
  currency_id: string
  country: Country
  company: Company
}

export async function getPayroll(userId: string): Promise<Payroll[]> {
  const data = await getData<any>({
    tables: ['pg_user_enabled_payrolls'],
    join: [
      'INNER JOIN payrolls ON user_enabled_payrolls.payroll_id = payrolls.id',
      'INNER JOIN currencies ON payrolls.currency_id = currencies.id',
      'INNER JOIN companies ON payrolls.company_id = companies.id',
      'INNER JOIN countries ON companies.country_id = countries.id'
    ],
    fields: [
      'payrolls.id',
      'payrolls.name',
      'payrolls.code',
      'payrolls.company_id as companyId',
      'companies.name as companyName',
      'companies.code as companyCode',
      'payrolls.currency_id as currencyId',
      'currencies.name as currencyName',
      'countries.id  as countryId',
      'countries.code as countryCode',
      'countries.iso_code as countryISOCode',
      'countries.name as countryName'
    ],
    where: [`user_enabled_payrolls.user_id = '${userId}'`]
  })
  return data[0].results.map((payroll: any) => {
    return {
      id: payroll.id,
      name: payroll.name,
      code: payroll.code,
      currency_id: payroll.currencyId,
      country: {
        id: payroll.countryId,
        code: payroll.countryCode,
        iso_code: payroll.countryISOCode,
        name: payroll.countryName
      },
      company: {
        id: payroll.companyId,
        name: payroll.companyName,
        code: payroll.companyCode
      }
    }
  })
}

export async function getPayrolls(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = ['payrolls.company_id=:companyId']
  if (search) {
    where.push(
      `CONCAT(payrolls.name,payrolls.code,frequencies.name,currencies.name,holiday_groups.name) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? dataOrder[1] === 'code'
      ? (modifier = [
          `ORDER BY (CASE WHEN payrolls.code ~ '^[0-9]+$' THEN CAST(payrolls.code AS NUMERIC) ELSE NULL END) ${dataOrder[0]}, payrolls.code ${dataOrder[0]}`
        ])
      : (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = [
        "ORDER BY (CASE WHEN payrolls.code ~ '^[0-9]+$' THEN CAST(payrolls.code AS NUMERIC) ELSE NULL END), payrolls.code"
      ])
  // result
  const response: any = await getData({
    tables: ['pg_payrolls'],
    fields: [
      'payrolls.id',
      'payrolls.code',
      'payrolls.name',
      'frequencies.name as frequency',
      'currencies.name as currency',
      'holiday_groups.name as holidayGroup',
      'payrolls.is_inactive',
      'payrolls.updated_at'
    ],
    join: [
      'INNER JOIN frequencies ON payrolls.frequency_id = frequencies.id',
      'INNER JOIN currencies ON payrolls.currency_id = currencies.id',
      'INNER JOIN holiday_groups ON payrolls.holiday_group_id = holiday_groups.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getPayrollData(id: string): Promise<Payroll> {
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields: [
      'id',
      'company_id as companyId',
      'code',
      'name',
      'currency_id as currencyId',
      'frequency_id as frequencyId',
      'working_days as workingDays',
      'holiday_group_id as holidayGroupId',
      'employee_code_type as employeeCodeType',
      'employee_code_consecutive as employeeCodeConsecutive',
      "TO_CHAR(last_payment,'yyyy-mm-dd') as lastPayment",
      "TO_CHAR(last_bonus,'yyyy-mm-dd') as lastBonus",
      'alter_names_concepts as alterNamesConcepts',
      'alter_codes_concepts as alterCodesConcepts',
      'send_receipt_email as sendReceiptEmail',
      'calendar_days as calendarDays',
      'custom_fields as custom'
    ],
    where: [`id = '${id}'`],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createPayroll(data: Payroll): Promise<boolean> {
  const response: any = await http.post('payroll/payrolls', {
    companyId: data.companyId,
    code: data.code,
    name: data.name,
    currencyId: data.currencyId,
    frequencyId: data.frequencyId,
    workingDays: data.workingDays,
    holidayGroupId: data.holidayGroupId,
    employeeCodeType: data.employeeCodeType,
    employeeCodeConsecutive: data.employeeCodeConsecutive,
    lastPayment: data.lastPayment,
    lastBonus: data.lastBonus,
    alterNamesConcepts: data.alterNamesConcepts,
    alterCodesConcepts: data.alterCodesConcepts,
    sendReceiptEmail: data.sendReceiptEmail,
    calendarDays: data.calendarDays,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updatePayroll(
  data: Payroll,
  id: string
): Promise<boolean> {
  const response: any = await http.put('payroll/payrolls/' + id, {
    companyId: data.companyId,
    code: data.code,
    name: data.name,
    currencyId: data.currencyId,
    frequencyId: data.frequencyId,
    workingDays: data.workingDays,
    holidayGroupId: data.holidayGroupId,
    employeeCodeType: data.employeeCodeType,
    employeeCodeConsecutive: data.employeeCodeConsecutive,
    lastPayment: data.lastPayment,
    lastBonus: data.lastBonus,
    alterNamesConcepts: data.alterNamesConcepts,
    alterCodesConcepts: data.alterCodesConcepts,
    sendReceiptEmail: data.sendReceiptEmail,
    calendarDays: data.calendarDays,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateStatePayroll(
  id: string,
  enable: boolean
): Promise<boolean> {
  const response: any = await http.put(`payroll/payrolls/${id}/enable/`, {
    enable
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deletePayroll(id: string): Promise<boolean> {
  const response: any = await http.delete('payroll/payrolls/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

// Interfaces para validación de código de nómina
export interface PayrollCodeUsage {
  companyCode: string
  payrollCode: string
}

export async function checkPayrollCodeAvailability(
  payrollCode: string
): Promise<PayrollCodeUsage[]> {
  const payload = {
    payrollCode
  }
  const response: any = await http.post(
    'payroll/payrolls/validate-code',
    payload
  )
  return response.data
}
